import type { UseFormReturn } from 'react-hook-form';
import { ShieldCheck } from 'lucide-react';
import { useCACertificatesForDropdown, useCertificatesForDropdown } from '../../../../hooks/useCertificates';
import { useTlsContexts } from '../../../../hooks/useTlsContexts';
import type { SipPeerFormData } from '../schema';
import { TLS_CIPHERS } from '../schema';

/**
 * TLS tab: outbound (client-mode) TLS configuration.
 * Only rendered when the selected SIP interface uses the TLS transport
 * (the editor hides this tab otherwise).
 */
export function TlsTab({ form }: { form: UseFormReturn<SipPeerFormData> }) {
  const { data: certificates, isLoading: certificatesLoading } = useCertificatesForDropdown();
  const { data: caCertificates, isLoading: caCertificatesLoading } = useCACertificatesForDropdown();
  const { data: tlsContexts = [], isLoading: tlsContextsLoading } = useTlsContexts();

  const selectedTlsContextId = form.watch('tlsContextId');
  const selectedTlsContext = tlsContexts.find((context) => context.id === selectedTlsContextId) ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Outbound TLS</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure TLS settings for outbound connections TO this peer (client mode).
        </p>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h4 className="font-medium text-gray-900 flex items-center">
          <ShieldCheck className="w-5 h-5 mr-2 text-green-600" />
          Outbound TLS Configuration
        </h4>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">TLS Context</label>
          <select
            {...form.register('tlsContextId')}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={tlsContextsLoading}
          >
            <option value="">{tlsContextsLoading ? 'Loading TLS contexts…' : 'Select a reusable TLS context'}</option>
            {tlsContexts.map((context) => (
              <option key={context.id} value={context.id}>
                {context.name}{context.enabled ? '' : ' (disabled)'}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Reusable policy attached to the peer for outbound TLS routing.
          </p>
          {selectedTlsContext ? (
            <div className="mt-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-900">
              Attached context: <span className="font-medium">{selectedTlsContext.name}</span>
              {!selectedTlsContext.enabled && ' (disabled)'}
            </div>
          ) : (
            <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              No TLS context selected. TLS peers require an attached enabled context.
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Certificate ID
            </label>
            <select
              {...form.register('outboundTlsConfig.certificateId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={certificatesLoading}
            >
              <option value="">No client certificate</option>
              {certificates?.map((cert) => (
                <option key={cert.value} value={cert.value}>{cert.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              CA Certificate ID
            </label>
            <select
              {...form.register('outboundTlsConfig.caCertificateId')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={caCertificatesLoading}
            >
              <option value="">System trust store</option>
              {caCertificates?.map((cert) => (
                <option key={cert.value} value={cert.value}>{cert.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum TLS Version
            </label>
            <select
              {...form.register('outboundTlsConfig.minVersion')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Default</option>
              <option value="TLSv1.2">TLS 1.2</option>
              <option value="TLSv1.3">TLS 1.3</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SNI (Server Name Indication)
            </label>
            <input
              {...form.register('outboundTlsConfig.sni')}
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Hostname for TLS handshake"
            />
          </div>
          <div className="flex items-center md:col-span-2">
            <input
              {...form.register('outboundTlsConfig.verifyServer')}
              type="checkbox"
              defaultChecked={true}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label className="ml-2 block text-sm text-gray-700">
              Verify peer&apos;s server certificate
            </label>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Cipher suites</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {TLS_CIPHERS.map((cipher) => {
                const selected = form.watch('outboundTlsConfig.ciphers') ?? [];
                return (
                  <label key={cipher} className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={selected.includes(cipher)}
                      onChange={(event) => {
                        const current = form.getValues('outboundTlsConfig.ciphers') ?? [];
                        const next = event.target.checked
                          ? Array.from(new Set([...current, cipher]))
                          : current.filter((item) => item !== cipher);
                        form.setValue('outboundTlsConfig.ciphers', next, { shouldDirty: true });
                      }}
                    />
                    <span className="font-mono text-xs">{cipher}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
